import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
    
    // You can also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">⚠️</div>
            <h2>Something went wrong</h2>
            <p>We're sorry, but something unexpected happened while processing your payment.</p>
            <div className="error-actions">
              <button 
                onClick={() => window.location.reload()} 
                className="error-boundary-button primary"
              >
                🔄 Reload Page
              </button>
              <button 
                onClick={() => window.history.back()} 
                className="error-boundary-button secondary"
              >
                ← Go Back
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error details (development only)</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>

          <style jsx>{`
            .error-boundary {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: var(--bg-primary, #fff);
              padding: 2rem;
            }

            .error-boundary-content {
              max-width: 500px;
              text-align: center;
              padding: 2rem;
              border-radius: 1rem;
              background: var(--bg-secondary, #f9fafb);
              border: 1px solid var(--border-color, #e5e7eb);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }

            .error-icon {
              font-size: 3rem;
              margin-bottom: 1rem;
            }

            .error-boundary-content h2 {
              color: var(--text-primary, #111);
              margin-bottom: 1rem;
              font-size: 1.5rem;
            }

            .error-boundary-content p {
              color: var(--text-secondary, #666);
              margin-bottom: 2rem;
              line-height: 1.5;
            }

            .error-actions {
              display: flex;
              gap: 1rem;
              justify-content: center;
              flex-wrap: wrap;
            }

            .error-boundary-button {
              padding: 0.75rem 1.5rem;
              border: none;
              border-radius: 0.5rem;
              font-size: 0.875rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }

            .error-boundary-button.primary {
              background: var(--accent-color, #6366f1);
              color: white;
            }

            .error-boundary-button.primary:hover {
              background: var(--accent-dark, #4f46e5);
            }

            .error-boundary-button.secondary {
              background: var(--bg-primary, #fff);
              color: var(--text-primary, #111);
              border: 1px solid var(--border-color, #e5e7eb);
            }

            .error-boundary-button.secondary:hover {
              background: var(--bg-tertiary, #f3f4f6);
            }

            .error-details {
              margin-top: 2rem;
              text-align: left;
            }

            .error-details summary {
              cursor: pointer;
              font-weight: 600;
              color: var(--text-secondary, #666);
              margin-bottom: 0.5rem;
            }

            .error-stack {
              background: var(--bg-primary, #fff);
              padding: 1rem;
              border-radius: 0.5rem;
              font-size: 0.75rem;
              overflow-x: auto;
              border: 1px solid var(--border-color, #e5e7eb);
            }

            @media (max-width: 640px) {
              .error-boundary {
                padding: 1rem;
              }

              .error-boundary-content {
                padding: 1.5rem;
              }

              .error-actions {
                flex-direction: column;
              }
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;